import { useEffect, useRef } from "react";

/**
 * Shared modal-dialog a11y behavior:
 *
 *   • Traps Tab / Shift+Tab focus inside the dialog element while ``open``
 *     is ``true``. Tab from the last focusable wraps to the first;
 *     Shift+Tab from the first wraps to the last.
 *   • Handles the Escape key — calling ``onClose`` (usually the same
 *     handler the Cancel button invokes) so the dialog can be dismissed
 *     from the keyboard.
 *   • Captures the ``document.activeElement`` at the moment the dialog
 *     opens and restores focus to it when the dialog closes, so keyboard
 *     users don't lose their place in the underlying page.
 *   • Moves initial focus into the dialog on open (first focusable
 *     descendant, typically the primary action or cancel button).
 *
 * Consumers pass:
 *   • ``open``    — whether the dialog is currently visible.
 *   • ``ref``     — a ref pointing at the dialog root element (the
 *                   element with ``role="dialog"``).
 *   • ``onClose`` — invoked when Escape is pressed. Cancel/Backdrop
 *                   handlers should call the same function so behaviour
 *                   is uniform.
 */
export function useDialogA11y(
  open: boolean,
  ref: React.RefObject<HTMLElement | null>,
  onClose: () => void,
) {
  const previousActive = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const dialog = ref.current;
    // Capture the currently-focused element so we can restore focus on
    // close (this element is the trigger button in nearly every case).
    previousActive.current =
      (document.activeElement as HTMLElement | null) ?? null;

    // Move initial focus into the dialog. Prefer the first focusable
    // descendant; if the dialog itself has ``tabIndex`` set (or is
    // programmatically focusable), fall back to focusing the dialog.
    const first = getFirstFocusable(dialog);
    if (first) first.focus();
    else if (dialog) {
      dialog.setAttribute("tabindex", "-1");
      (dialog as HTMLElement).focus();
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === "Tab" && dialog) {
        const focusables = getFocusables(dialog);
        if (focusables.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && (active === first || !dialog.contains(active))) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && (active === last || !dialog.contains(active))) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      // Restore focus to the previously-focused element. Only do this
      // if the previously-focused element is still connected — a
      // rendering change during dialog lifetime may have removed it.
      const prior = previousActive.current;
      if (prior && document.contains(prior)) {
        try { prior.focus(); } catch { /* noop */ }
      }
      previousActive.current = null;
    };
    // We intentionally depend on ``open`` only — the ref and onClose are
    // stable per-render for these dialogs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type=hidden])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function getFocusables(container: HTMLElement | null): HTMLElement[] {
  if (!container) return [];
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter((el) => !el.hasAttribute("disabled"));
}

function getFirstFocusable(container: HTMLElement | null): HTMLElement | null {
  return getFocusables(container)[0] ?? null;
}
